// @ts-nocheck -- matches session-recorder.ts, which is not yet typed (RARE-265)

/**
 * RARE-302 / RARE-303: somewhere for a batch to go when the buffer cannot have
 * it back.
 *
 * A refused flush normally costs nothing but a delay, because the events go
 * back in the buffer and the next flush carries them. There is one flush where
 * that is not available: the last one. `stopRecording` detaches the buffer and
 * clears the session id synchronously, then puts the final flush on the chain,
 * so by the time a refusal comes back there is no buffer belonging to that
 * recording to return to. Requeueing into whatever recording started next would
 * upload one session's events under another session's id, which is worse than
 * losing them, so those events were counted as abandoned and that was that.
 *
 * They are also the events most worth keeping. The final flush of a session is
 * the largest, it is the only one with no subsequent flush to compensate, and
 * when a recording is short it is the flush carrying the Meta and FullSnapshot
 * as well.
 *
 * So an unrequeueable batch is written here instead, and the next
 * `startRecording` in the same tab picks it up and posts it under its own
 * session id and its own sequence number. The chunk lands in the recording it
 * belongs to rather than the new one, which is the property the in-memory
 * requeue could not offer.
 *
 * `sessionStorage`, not `localStorage`, for two reasons. It is scoped to the
 * tab and survives a reload and a same-tab navigation, which is the lifetime of
 * the case this exists for. And it is cleared when the tab closes, so one
 * user's recorded DOM is not left sitting on disk for whoever opens the browser
 * next; a full rrweb snapshot of an authenticated page is not something to
 * persist indefinitely.
 *
 * This module knows about storage and nothing about rrweb. Deciding which
 * events a batch can afford to lose is the recorder's business, so the caller
 * trims to `MAX_BATCH_BYTES` before handing the batch over.
 *
 * Every entry point here is total: storage can be disabled, full, or holding a
 * value written by an older version of this format, and none of those may throw
 * into the recorder. A failure to persist is reported to the caller as 0 so it
 * can count the events as lost honestly, rather than swallowed as if the batch
 * had been saved.
 */

const STORAGE_KEY = "rare:recorder:pending-batches:v1";

// At most this many batches held at once, newest kept. More than a couple means
// delivery has been failing across several recordings in one tab, and the older
// ones are the least likely to be accepted and the least useful if they are.
const MAX_RECORDS = 3;

// A batch refused this many times is being refused for a reason that will not
// change by trying again: a body over the server's limit, a token the user will
// never hold again, a session the recorder has since rejected. Retrying it
// forever would spend a request on every page load for the life of the tab.
const MAX_ATTEMPTS = 3;

// A backstop for a tab left open for days. sessionStorage already bounds the
// lifetime to the tab, so this only matters for the long-lived ones.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// What one batch may occupy. sessionStorage is a per-origin allowance of a few
// megabytes shared with the rest of the app, so the recorder takes a stated
// slice of it rather than as much as it can get.
export const MAX_BATCH_BYTES = 1024 * 1024;

function getStore() {
  try {
    // Reading the property itself throws in a sandboxed iframe and when storage
    // is disabled by policy, so the access is inside the try, not just the use.
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function isWellFormed(record) {
  return (
    record !== null &&
    typeof record === "object" &&
    typeof record.sessionId === "string" &&
    Number.isInteger(record.sequenceNumber) &&
    Array.isArray(record.events) &&
    record.events.length > 0
  );
}

function isStillWorthRetrying(record, now) {
  return (record.attempts ?? 0) < MAX_ATTEMPTS && now - (record.storedAt ?? 0) < MAX_AGE_MS;
}

function readRecords(store) {
  let raw;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return [];
  }
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isWellFormed) : [];
  } catch {
    // Truncated, or written by something else under the same key. Leaving it
    // would break every later read too, so it goes.
    try {
      store.removeItem(STORAGE_KEY);
    } catch {
      // Nothing further to try.
    }
    return [];
  }
}

// Writes the whole list, shedding the oldest records until it fits. A quota
// error is the expected failure here, not an exceptional one: the recorder is
// asking to store a DOM snapshot in a store the application is also using.
// Reports whether anything was written.
function writeRecords(store, records) {
  const remaining = records.slice();

  while (true) {
    try {
      if (remaining.length === 0) {
        store.removeItem(STORAGE_KEY);
        return false;
      }
      store.setItem(STORAGE_KEY, JSON.stringify(remaining));
      return true;
    } catch {
      if (remaining.length === 0) return false;
      remaining.shift();
    }
  }
}

/**
 * Holds a batch for the next recording in this tab to retry.
 *
 * `sequenceNumber` is the number the batch had already reserved. It is kept
 * rather than reissued because the counter it came from went with the recording
 * that ended, and the refusal handed the number straight back to it, so nothing
 * else will ever claim it. Reusing it puts the chunk at the key it was always
 * going to have.
 *
 * Returns the number of events actually stored, which is 0 when nothing could
 * be. The caller needs that: events which could not be written are lost, and
 * saying otherwise would repeat the exact reporting failure these tickets exist
 * to close.
 */
export function savePendingBatch({
  sessionId,
  userId,
  sequenceNumber,
  pageUrl,
  hasErrors,
  events,
  attempts = 0,
}) {
  const store = getStore();
  if (!store || events.length === 0) return 0;

  const now = Date.now();
  const record = {
    // Identifies this record across the write-and-verify below. Session id and
    // sequence number alone are not unique across a retry, and a timestamp
    // alone is not unique across two saves in the same millisecond.
    id: `${sessionId}:${sequenceNumber}:${attempts}:${now}`,
    sessionId,
    // Normalised, because JSON drops an `undefined` property and the take-side
    // comparison would then miss its own record on an anonymous recording.
    userId: userId ?? null,
    sequenceNumber,
    pageUrl,
    hasErrors: Boolean(hasErrors),
    events,
    attempts,
    storedAt: now,
  };

  const existing = readRecords(store).filter((candidate) => isStillWorthRetrying(candidate, now));
  const records = [...existing, record].slice(-MAX_RECORDS);

  if (!writeRecords(store, records)) return 0;

  // The record can still have been shed by `writeRecords` to make the write
  // fit, in which case nothing of this batch was stored and the caller must not
  // be told otherwise.
  return readRecords(store).some((candidate) => candidate.id === record.id) ? events.length : 0;
}

/**
 * Removes and returns the batches this user may retry, leaving every other
 * still-valid record in place.
 *
 * Scoped by user because the recorder posts the batch under the authenticated
 * user's token, and ingest returns 403 when the batch's user id does not match
 * the caller. A batch belonging to someone else would fail every time and burn
 * its attempts doing it, so it waits instead. On the case this exists for, a
 * page reload, the user is the same one and it is picked up immediately.
 *
 * Taken rather than copied: a retry that fails saves itself again with its
 * attempt count raised, which is what bounds the retrying. Leaving the record
 * in place would instead mean two copies of the same events in flight.
 */
export function takePendingBatches(userId) {
  const store = getStore();
  if (!store) return { batches: [], discardedEvents: 0 };

  const now = Date.now();
  const records = readRecords(store);
  const batches = [];
  const keep = [];
  let discardedEvents = 0;

  for (const record of records) {
    if (!isStillWorthRetrying(record, now)) {
      discardedEvents += record.events.length;
      continue;
    }
    if ((record.userId ?? null) === (userId ?? null)) {
      batches.push(record);
    } else {
      keep.push(record);
    }
  }

  if (batches.length > 0 || discardedEvents > 0) {
    writeRecords(store, keep);
  }

  return { batches, discardedEvents };
}
